/* eslint-disable max-lines-per-function */
import { ICellRendererParams } from '@ag-grid-community/core';
import moment from 'moment';
import { firstValueFrom } from 'rxjs';

import { Batch } from '../../../../order/models/batch';
import { DocumentService } from '../../../../commercial-documents/services/documents.service';
import { Order } from '../../../../order/models/order';
import { UserDatacenterView } from '../../../models/datacenter-aggrid-view';
import { TranslationService } from '../../../services/translation.service';
import {
	CommonColDef,
	DateColDef,
	DimensionColDef,
	IconColDef,
	LinkColDef,
	NumericColDef
} from '../../column-defs';
import { ColumnType } from '../../column-type';
import { DatacenterDateFilterComponent } from '../../filter-type/datacenter-date-filter.component';
import { DatacenterNumberFilterComponent } from '../../filter-type/datacenter-number-filter.component';
import { DatacenterOrderNumberFilterComponent } from '../../filter-type/datacenter-order-number-filter.component';
import { DatacenterTextFilterComponent } from '../../filter-type/datacenter-text-filter.component';
import { DatacenterDropdownFilterComponent } from '../../filter-type/datacenter-dropdown-filter.component';
import { UserService } from '../../../services/user.service';
import { DatacenterDimensionFilterComponent } from '../../filter-type/datacenter-dimension-filter.component';
import { DatacenterSuggestiveListFilterComponent } from '../../filter-type/datacenter-suggestive-list-filter.component';
import { ORDER_SERVER_URL } from '../../../../config';

const GET_DATACENTER_BATCHES_URL_V5 = `${ ORDER_SERVER_URL }/v5/datacenterBatches`;

const linkToOrderCardCellRendererParams = {
	routerLink(params: ICellRendererParams<Order, unknown>) {
		if (!params.data) {
			return null;
		}
		const { data } = params;
		return {
			commands: [
				'/order-tracking',
				'order',
				'card',
				data.supplierOrderNumber,
				data.supplierOrderItemNumber
			]
		};
	}
};

const linkToBatchCardCellRendererParams = {
	routerLink(params: ICellRendererParams<Batch, unknown>) {
		if (!params.data) {
			return '';
		}
		const batch = params.data;
		return {
			commands: [
				'/order-tracking',
				'batch',
				'card',
				batch.id
			]
		};
	}
};

export class BatchDatacenterColumnDefinition {
	readonly BATCH_REQUIRED_FIELDS = [
		'id',
		'supplierOrderNumber',
		'supplierOrderItemNumber',
		'supplierInvoiceNumber',
		'invoiceDate'
	];

	readonly BATCH_DATA_CENTER_DEFAULT_CONFIGURATION: Map<string, CommonColDef<Batch> | CommonColDef<Batch, string>>;

	constructor(
		private readonly translationService: TranslationService,
		private readonly userService: UserService,
		private readonly documentService: DocumentService
	) {
		this.BATCH_DATA_CENTER_DEFAULT_CONFIGURATION = new Map<
			string,
			CommonColDef<Batch> | CommonColDef<Batch, string>
		>([
			[
				'batchReference',
				new LinkColDef<Batch>({
					headerName: this.translationService.getTR('filter.batchid'),
					colId: 'batchReference',
					field: 'batchReference',
					cellRendererParams: linkToBatchCardCellRendererParams,
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],

			[
				'coating',
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.coating'),
					field: 'coating',
					colId: 'coating',
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const batch = params.data;
						return batch.coatingDescription;
					},
					filter: DatacenterDropdownFilterComponent,
					filterParams: { batchFields: ['coating', 'coatingDescription'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					sortable: false
				})
			],
			[
				'customerOrderNumber',
				new LinkColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.customer-order-number'),
					colId: 'customerOrderNumber',
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const batch = params.data;
						if (!batch.customerOrderNumber) {
							return '';
						}
						let value = batch.customerOrderNumber;
						if (batch.customerOrderItemNumber) {
							value += `/${ batch.customerOrderItemNumber }`;
						}
						return value;
					},
					fields: ['customerOrderNumber', 'customerOrderItemNumber'],
					filter: DatacenterOrderNumberFilterComponent,
					minWidth: 150,
					cellRendererParams: linkToOrderCardCellRendererParams,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.ORDER_NUMBER_COLUMN
				})
			],
			[
				'serviceLevel',
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.service-level'),
					field: 'serviceLevel',
					colId: 'serviceLevel',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { batchFields: ['serviceLevel', 'serviceLevelDescription'] },
					suppressHeaderMenuButton: false,
					sortable: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const batch = params.data;
						return batch.serviceLevelDescription;
					}
				})
			],
			[
				'carrierName',
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.carrier-name'),
					colId: 'carrierName',
					field: 'carrierName',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { batchFields: ['carrierName', 'carrierName'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					sortable: true,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const batchData = params.data;
						return batchData.carrierName;
					}
				})
			],
			[
				'mill',
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.mill'),
					field: 'millDescription',
					colId: 'mill',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'steelgrade',
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.standard-steel-grade'),
					field: 'steelgradeDescription',
					colId: 'steelgrade',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const batch = params.data;
						return batch.steelgradeDescription;
					},
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'qtyGross',
				new NumericColDef<Batch>({
					headerName: this.translationService.getTR('datacenter.qty-gross'),
					colId: 'qtyGross',
					field: 'qtyBatchGross',
					cellRendererParams: { numericFormat: 'weight-with-zero' },
					filter: DatacenterNumberFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'qtyNet',
				new NumericColDef<Batch>({
					headerName: this.translationService.getTR('datacenter.qty-net'),
					colId: 'qtyNet',
					field: 'qtyBatchNet',
					cellRendererParams: { numericFormat: 'weight-with-zero' },
					filter: DatacenterNumberFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'nbDaysInStock',
				new NumericColDef<Batch>({
					headerName: this.translationService.getTR('datacenter.nb-days-in-stock'),
					colId: 'nbDaysInStock',
					field: 'daysInStock',
					cellRendererParams: { numericFormat: 'number' },
					filter: DatacenterNumberFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'currentLocation',
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.current-location'),
					field: 'currentLocationDescription',
					colId: 'currentLocation',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'soldTo',
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.sold-to'),
					field: 'soldToDescription',
					colId: 'soldTo',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'supplierOrderNumber',
				new LinkColDef<Batch>({
					headerName: this.translationService.getTR('order.supplier.order.number'),
					colId: 'supplierOrderNumber',
					valueGetter: params => this.getSupplierData(params.data),
					fields: ['supplierOrderNumber', 'supplierOrderItemNumber'],
					minWidth: 150,
					cellRendererParams: linkToOrderCardCellRendererParams,
					filter: DatacenterOrderNumberFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.ORDER_NUMBER_COLUMN
				})
			],
			[
				'customerArticleReference',
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.customer-article-reference'),
					field: 'customerArticleReference',
					colId: 'customerArticleReference',
					filter: DatacenterSuggestiveListFilterComponent,
					filterParams: {
						batchFields: ['customerArticleReference', 'customerArticleReference'],
						url: GET_DATACENTER_BATCHES_URL_V5
					},
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					sortable: true,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const batch = params.data;
						return batch.customerArticleReference;
					}
				})
			],
			[
				'choice',
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.choice'),
					field: 'choiceDescription',
					colId: 'choice',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { batchFields: ['choice', 'choiceDescription'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					sortable: true,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const batch = params.data;
						return batch.choiceDescription;
					}
				})
			],
			[
				'form',
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.form'),
					field: 'formDescription',
					colId: 'form',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { batchFields: ['form', 'formDescription'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					sortable: true,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const batch = params.data;
						return batch.formDescription;
					}
				})
			],
			[
				'orderItemStatus',
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.order-item-status'),
					field: 'orderItemStatusDescription',
					colId: 'orderItemStatus',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { batchFields: ['orderItemStatus', 'orderItemStatusDescription'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					sortable: true,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const batch = params.data;
						return batch.orderItemStatusDescription;
					}
				})
			],
			[
				'productDescription',
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.product-description'),
					field: 'productDescription',
					colId: 'productDescription',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { batchFields: ['productDescription', 'productDescription'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					sortable: true,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const batch = params.data;
						return batch.productDescription;
					}
				})
			],
			[
				'choice',
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.choice'),
					field: 'choiceDescription',
					colId: 'choice',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { batchFields: ['choice', 'choiceDescription'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					sortable: true
				})
			],
			[
				'form',
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.form'),
					field: 'formDescription',
					colId: 'form',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { batchFields: ['form', 'formDescription'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					sortable: true
				})
			],
			[
				'orderItemStatus',
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.order-item-status'),
					field: 'orderItemStatusDescription',
					colId: 'orderItemStatus',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { batchFields: ['orderItemStatus', 'orderItemStatusDescription'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					sortable: true
				})
			],
			[
				'productDescription',
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.product-description'),
					field: 'productDescription',
					colId: 'productDescription',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { batchFields: ['productDescription', 'productDescription'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					sortable: true
				})
			],
			[
				'dispatchAdviceNumber',
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.dispatch-advice-number'),
					field: 'lastDispatchAdviceNumber',
					colId: 'dispatchAdviceNumber',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'dispatchAdviceDate',
				new DateColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.dispatch-advice-date'),
					field: 'lastDispatchAdviceDate',
					colId: 'dispatchAdviceDate',
					filter: DatacenterDateFilterComponent,
					suppressHeaderMenuButton: false,
					cellRendererParams: { inputFormat: 'isoDate', outputFormat: 'date' },
					columnType: ColumnType.DATE_COLUMN
				})
			],
			[
				'statusGroup',
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('batch.status'),
					field: 'statusGroup',
					colId: 'statusGroup',
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const batch = params.data;
						return batch.statusGroupDescription;
					},
					filter: DatacenterDropdownFilterComponent,
					filterParams: { batchFields: ['statusGroup', 'statusGroupDescription'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					sortable: false
				})
			],
			[
				'status',
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.detailed-batch-status'),
					field: 'status',
					colId: 'status',
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const batch = params.data;
						return batch.statusDescription;
					},
					filter: DatacenterDropdownFilterComponent,
					filterParams: { batchFields: ['status', 'statusDescription'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					sortable: true
				})
			],
			[
				'batchStatusCategory',
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('batch.in-transit-detail'),
					field: 'batchStatusCategory',
					colId: 'batchStatusCategory',
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const batch = params.data;
						return batch.batchStatusCategoryDescription;
					},
					filter: DatacenterDropdownFilterComponent,
					filterParams: { batchFields: ['batchStatusCategory', 'batchStatusCategoryDescription'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					sortable: false
				})
			],
			[
				'readyToShipDate',
				new DateColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.date-available-for-dispatch'),
					colId: 'readyToShipDate',
					field: 'readyToShipDate',
					minWidth: 120,
					maxWidth: 120,
					width: 120,
					cellRendererParams: { inputFormat: 'isoDate', outputFormat: 'date' },
					filter: DatacenterDateFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.DATE_COLUMN
				})
			],
			[
				'dimension',
				new DimensionColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.dimension'),
					dimensionGetter(params) {
						return params.data;
					},
					colId: 'dimension',
					fields: ['thickness', 'width', 'length'],
					filter: DatacenterDimensionFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.DIMENSION_COLUMN
				})
			],
			[
				'length',
				new NumericColDef<Batch>({
					headerName: this.translationService.getTR('batch-card.length'),
					field: 'length',
					colId: 'length',
					filter: DatacenterNumberFilterComponent,
					cellRendererParams: { numericFormat: 'length-mm' },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.NUMBER_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const batch = params.data;
						if (!batch.length) {
							return '';
						}
						return batch.length;
					},
				})
			],
			[
				'millOrderNumber',
				new LinkColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.mill-order-number'),
					colId: 'millOrderNumber',
					valueGetter: params => this.getMillData(params.data),
					fields: ['millOrderNumber', 'millOrderItemNumber'],
					minWidth: 150,
					cellRendererParams: linkToOrderCardCellRendererParams,
					filter: DatacenterOrderNumberFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.ORDER_NUMBER_COLUMN
				})
			],
			[
				'shipTo',
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.ship-to'),
					field: 'shipToDescription',
					colId: 'shipTo',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'thickness',
				new NumericColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.thickness'),
					field: 'thickness',
					colId: 'thickness',
					filter: DatacenterNumberFilterComponent,
					cellRendererParams: { numericFormat: 'thickness' },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'width',
				new NumericColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.width'),
					field: 'width',
					colId: 'width',
					filter: DatacenterNumberFilterComponent,
					cellRendererParams: { numericFormat: 'width' },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'cdd',
				new DateColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.cdd'),
					colId: 'cdd',
					field: 'cddWeek',
					minWidth: 120,
					maxWidth: 120,
					width: 120,
					valueGetter: params => {
						if (!params.data?.cddWeek || params.data.cddWeek.includes('1900-W01')) {
							return '';
						}
						return params.data.cddWeek;
					},
					cellRendererParams: { inputFormat: 'isoWeek', outputFormat: 'week' },
					filter: DatacenterDateFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.DATE_COLUMN
				})
			],
			[
				'colorBottomSide',
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.color-bottom-side'),
					field: 'colorBottomSide',
					colId: 'colorBottomSide',
					sortable: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					filter: DatacenterDropdownFilterComponent,
					filterParams: { batchFields: ['colorBottomSide', 'colorBottomSideDescription'] },
					suppressHeaderMenuButton: false,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const batch = params.data;
						return batch.colorBottomSideDescription;
					},
				})
			],
			[
				'colorTopSide',
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.color-top-side'),
					field: 'colorTopSide',
					colId: 'colorTopSide',
					columnType: ColumnType.TRANSLATED_COLUMN,
					filter: DatacenterDropdownFilterComponent,
					filterParams: { batchFields: ['colorTopSide', 'colorTopSideDescription'] },
					suppressHeaderMenuButton: false,
					sortable: false,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const batch = params.data;
						return batch.colorTopSideDescription;
					},
				})
			],
			[
				'heatNumber',
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.heat-number'),
					field: 'heatNumber',
					colId: 'heatNumber',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'packageSerialNumber',
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.package-serial-number'),
					field: 'packageSerialNumber',
					colId: 'packageSerialNumber',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'plannedShipmentDate',
				new DateColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.planned-shipment-date'),
					colId: 'plannedShipmentDate',
					field: 'plannedShipmentDate',
					minWidth: 120,
					maxWidth: 120,
					width: 120,
					cellRendererParams: { inputFormat: 'isoDate', outputFormat: 'date' },
					filter: DatacenterDateFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.DATE_COLUMN
				})
			],
			[
				'loadingProgramNumber',
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.loading-program-number'),
					colId: 'loadingProgramNumber',
					field: 'loadingProgramNumber',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'transportReference',
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.transport-reference'),
					colId: 'transportReference',
					field: 'transportReference',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'endUser',
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.end-user'),
					field: 'endUserDescription',
					colId: 'endUser',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'supplierInvoiceNumber',
				new LinkColDef<Batch>({
					headerName: this.translationService.getTR('batch.supplier.invoice.number'),
					field: 'supplierInvoiceNumber',
					colId: 'supplierInvoiceNumber',
					fields: ['invoiceDate'],
					cellRendererParams: {
						routerLink: (params: ICellRendererParams<Batch, unknown>) => {
							const batch = params.data;
							return batch?.supplierInvoiceNumber && batch.invoiceDate ? {
								commands: ['/commercial-documents/cards/invoice-card'],
								extras: {
									queryParams: {
										invoiceNumber: batch.supplierInvoiceNumber,
										invoiceDate: batch.invoiceDate
									}
								}
							} : undefined;
						}
					},
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'downloadInvoice',
				new IconColDef<Batch>({
					headerName: this.translationService.getTR('datacenter.download-invoice'),
					colId: 'downloadInvoice',
					minWidth: 100,
					maxWidth: 100,
					width: 100,
					fields: ['supplierInvoiceNumber', 'invoiceDate'],
					cellRendererParams: {
						icon: (params: ICellRendererParams<Batch, unknown>) => params.data?.supplierInvoiceNumber
							? { iconName: 'table_document' } : undefined,
						onClick: (params: ICellRendererParams<Batch, unknown>) => async () => {
							await this.downloadInvoice(params.data);
						}
					}
				})
			],
			[
				'invoiceDate',
				new DateColDef<Batch>({
					headerName: this.translationService.getTR('batch.invoice.date'),
					colId: 'invoiceDate',
					field: 'invoiceDate',
					minWidth: 120,
					maxWidth: 120,
					width: 120,
					filter: DatacenterDateFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.DATE_COLUMN
				})
			],
			[
				'deliveryMode',
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('filter.deliveryMode'),
					field: 'deliveryMode',
					colId: 'deliveryMode',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { batchFields: ['deliveryMode', 'deliveryModeDescription'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					sortable: false,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const batch = params.data;
						return batch.deliveryModeDescription;
					}
				})
			],
			[
				'supplierOrderType',
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.spot-or-schedule'),
					field: 'supplierOrderType',
					colId: 'supplierOrderType',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { batchFields: ['supplierOrderType'], removeNoValueFilter: true },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					sortable: false,
					multiFilterCodes: new Map<string, string[]>([['spot', ['C']], ['scheduled', ['E', 'F']]]),
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const batch = params.data;
						if (batch.supplierOrderType === 'C') {
							return this.translationService.getTR('filter.spot-order');
						}
						if (batch.supplierOrderType === 'E' || batch.supplierOrderType === 'F') {
							return this.translationService.getTR('filter.schedule-business-order');
						}
						return '';
					}
				})
			],
			[
				'transportMode',
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.transport-mode'),
					field: 'transportMode',
					colId: 'transportMode',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { batchFields: ['transportMode', 'transportModeDescription'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					sortable: false,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const batch = params.data;
						return batch.transportModeDescription;
					}
				})
			],
			[
				'shipToAddress',
				new CommonColDef<Batch>({
					colId: 'shipToAddress',
					filter: DatacenterTextFilterComponent,
					filterParams: { suppressAndOrCondition: true },
					isMandatoryOnLeft: false,
					sortable: true,
					headerName: this.translationService.getTR('datacenter-filter.destination-address'),
					field: 'shipToAddress',
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const batch = params.data;
						return batch.shipToAddress;
					}
				})
			],
			[
				'destinationName',
				new CommonColDef<Batch>({
					colId: 'destinationName',
					headerName: this.translationService.getTR('datacenter-filter.destination-name'),
					field: 'shipToDescription',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'shipmentReferenceDate',
				new DateColDef<Batch>({
					colId: 'shipmentReferenceDate',
					filter: DatacenterDateFilterComponent,
					filterParams: { suppressAndOrCondition: true },
					isMandatoryOnLeft: false,
					sortable: true,
					headerName: this.translationService.getTR('shipment.dispatch-date'),
					field: 'shipmentReferenceDate',
					suppressHeaderMenuButton: false,
					columnType: ColumnType.DATE_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const batch = params.data;
						return batch.shipmentReferenceDate;
					}
				})
			],
			[
				'salesQuarter',
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.sales-quarter'),
					field: 'salesQuarter',
					colId: 'salesQuarter',
					filter: DatacenterTextFilterComponent,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const batch = params.data;
						return batch.salesQuarter;
					},
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'destinationByBoat',
				new CommonColDef<Batch>({
					colId: 'destinationByBoat',
					filter: DatacenterTextFilterComponent,
					filterParams: { suppressAndOrCondition: true },
					isMandatoryOnLeft: false,
					sortable: true,
					headerName: this.translationService.getTR('datacenter-filter.destination-by-boat'),
					field: 'destinationByBoat',
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const batch = params.data;
						return batch.destinationByBoat;
					}
				})
			],
			[
				'productCategorySTU',
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.product-category'),
					field: 'productCategorySTU',
					colId: 'productCategorySTU',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { batchFields: ['productCategorySTU', 'productCategorySTUDescription'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					sortable: true,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const batch = params.data;
						return batch.productCategorySTUDescription;
					}
				})
			],
			[
				'rddWeek',
				new DateColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.rdd-week'),
					colId: 'rddWeek',
					field: 'rddWeek',
					minWidth: 120,
					maxWidth: 120,
					width: 120,
					valueGetter: params => {
						if (!params.data?.rddWeek) {
							return '';
						}
						return params.data.rddWeek;
					},
					cellRendererParams: { inputFormat: 'isoWeek', outputFormat: 'week' },
					filter: DatacenterDateFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.DATE_COLUMN
				})
			],
			[
				'shipmentSectionN-1',
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('filter.shipment-section-n1'),
					colId: 'shipmentSectionN-1',
					hide: true,
					sortable: false,
				})
			],
			[
				'dispatchAdviceDateN1',
				new DateColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.dispatch-advice-date-n1'),
					field: 'dispatchAdviceDateN1',
					colId: 'dispatchAdviceDateN1',
					groupId: 'shipmentSectionN-1',
					suppressHeaderMenuButton: false,
					cellRendererParams: { inputFormat: 'isoDate', outputFormat: 'date' },
					columnType: ColumnType.DATE_COLUMN,
					filter: DatacenterDateFilterComponent,
					sortable: true,
					valueGetter: params => {
						if (!params.data?.dispatchAdviceDateN1) {
							return '';
						}
						return params.data.dispatchAdviceDateN1;
					},
				})
			],
			[
				'dispatchAdviceNumberN1',
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.dispatch-advice-number-n1'),
					field: 'dispatchAdviceNumberN1',
					colId: 'dispatchAdviceNumberN1',
					groupId: 'shipmentSectionN-1',
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					filter: DatacenterTextFilterComponent,
					sortable: true,
					valueGetter: params => {
						if (!params.data?.dispatchAdviceNumberN1) {
							return '';
						}
						return params.data.dispatchAdviceNumberN1;
					},
				})
			],
			[
				'transportModeN1',
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.transport-mode-n1'),
					field: 'transportModeN1',
					colId: 'transportModeN1',
					groupId: 'shipmentSectionN-1',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { batchFields: ['transportModeN1', 'transportModeN1Description'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					sortable: true,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const batch = params.data;
						return batch.transportModeN1Description;
					}
				})
			],
			[
				'transportReferenceN1',
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.transport-reference-n1'),
					colId: 'transportReferenceN1',
					field: 'transportReferenceN1',
					groupId: 'shipmentSectionN-1',
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					filter: DatacenterTextFilterComponent,
					sortable: true,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const batch = params.data;
						return batch.transportReferenceN1;
					}
				})
			],
			[
				'shipmentSectionN-2',
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('filter.shipment-section-n2'),
					colId: 'shipmentSectionN-2',
					hide: true,
					sortable: false,
				})
			],
			[
				'dispatchAdviceDateN2',
				new DateColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.dispatch-advice-date-n2'),
					field: 'dispatchAdviceDateN2',
					colId: 'dispatchAdviceDateN2',
					groupId: 'shipmentSectionN-2',
					suppressHeaderMenuButton: false,
					filter: DatacenterDateFilterComponent,
					cellRendererParams: { inputFormat: 'isoDate', outputFormat: 'date' },
					columnType: ColumnType.DATE_COLUMN,
					valueGetter: params => {
						if (!params.data?.dispatchAdviceDateN2) {
							return '';
						}
						return params.data.dispatchAdviceDateN2;
					},
				})
			],
			[
				'dispatchAdviceNumberN2',
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.dispatch-advice-number-n2'),
					field: 'dispatchAdviceNumberN2',
					colId: 'dispatchAdviceNumberN2',
					groupId: 'shipmentSectionN-2',
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					filter: DatacenterTextFilterComponent,
					valueGetter: params => {
						if (!params.data?.dispatchAdviceNumberN2) {
							return '';
						}
						return params.data.dispatchAdviceNumberN2;
					},
				})
			],
			[
				'transportModeN2',
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.transport-mode-n2'),
					field: 'transportModeN2',
					colId: 'transportModeN2',
					groupId: 'shipmentSectionN-2',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { batchFields: ['transportModeN2', 'transportModeN2Description'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					sortable: true,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const batch = params.data;
						return batch.transportModeN2Description;
					}
				})
			],
			[
				'transportReferenceN2',
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.transport-reference-n2'),
					colId: 'transportReferenceN2',
					field: 'transportReferenceN2',
					groupId: 'shipmentSectionN-2',
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					filter: DatacenterTextFilterComponent,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const batch = params.data;
						return batch.transportReferenceN2;
					}
				})
			],
			[
				'producedDimension',
				new DimensionColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.producedDimension'),
					dimensionGetter(params) {
						return params.data;
					},
					colId: 'producedDimension',
					fields: ['producedThickness', 'producedWidth', 'producedLength'],
					filter: DatacenterDimensionFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.DIMENSION_COLUMN
				})
			],
			[
				'producedLength',
				new NumericColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.producedLength'),
					field: 'producedLength',
					colId: 'producedLength',
					filter: DatacenterNumberFilterComponent,
					cellRendererParams: { numericFormat: 'length-mm-2-deci' },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.NUMBER_COLUMN,
					valueGetter: params => {
						if (!params.data || params.data.form !== '04' || !params.data.producedLength) {
							return '';
						}
						return Number(params.data.producedLength) / 1000;
					},
				})
			],
			[
				'producedThickness',
				new NumericColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.producedThickness'),
					field: 'producedThickness',
					colId: 'producedThickness',
					filter: DatacenterNumberFilterComponent,
					cellRendererParams: { numericFormat: 'thickness' },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'producedWidth',
				new NumericColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.producedWidth'),
					field: 'producedWidth',
					colId: 'producedWidth',
					filter: DatacenterNumberFilterComponent,
					cellRendererParams: { numericFormat: 'width' },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'serviceRequest',
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.service-request'),
					field: 'serviceRequest',
					colId: 'serviceRequest',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { batchFields: ['serviceRequest', 'serviceRequestDescription'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					sortable: false,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const batch = params.data;
						return batch.serviceRequestDescription;
					}
				})
			],
			[
				'stockAlert',
				new IconColDef<Batch>({
					headerName: this.translationService.getTR('order.stock-alert'),
					colId: 'stockAlert',
					field: 'stockAlert',
					minWidth: 50,
					maxWidth: 50,
					width: 50,
					filterParams: { removeNoValueFilter: true },
					suppressHeaderMenuButton: false,
					filter: DatacenterDropdownFilterComponent,
					columnType: ColumnType.COLOUR_COLUMN,
					cellRendererParams: {
						icon: (params: ICellRendererParams<Order>) => {
							const batch = params.data;
							if (!batch) {
								return '';
							}
							switch (batch.stockAlert) {
							case '1':
								return {
									iconName: 'in_stock',
									toolTipText: this.translationService.getTR('stock-alert.green')
								};
							case '2':
								return {
									iconName: 'in_stock_orange',
									toolTipText: this.translationService.getTR('stock-alert.orange')
								};
							case '3':
								return {
									iconName: 'in_stock_red',
									toolTipText: this.translationService.getTR('stock-alert.red')
								};
							default:
								return { iconName: '', displayEmpty: true };
							}
						},
						onClick() {
							// noop
						},
					},
					sortable: false,
				})
			]
		]);
	}

	addEcallBatchFields() {
		const hasAccess = this.userService.user.hasService('PCALL');
		if (hasAccess) {
			this.BATCH_DATA_CENTER_DEFAULT_CONFIGURATION.set(
				'ecallMillBatchReference',
				new CommonColDef<Batch>({
					autoHeight: true,
					headerName: this.translationService.getTR('ecall-off.supplier-call-off-reference'),
					valueGetter: params => {
						const batch = params.data;
						if (!batch) {
							return '';
						}
						return batch.ecallMillBatchReference;
					},
					field: 'ecallMillBatchReference',
					colId: 'ecallMillBatchReference',
					minWidth: 150,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					filter: DatacenterTextFilterComponent,
					filterParams: { batchFields: ['ecallMillBatchReference'], removeNoValueFilter: true },

				})
			);
			this.BATCH_DATA_CENTER_DEFAULT_CONFIGURATION.set(
				'rowCreationDate',
				new CommonColDef<Batch>({
					autoHeight: true,
					headerName: this.translationService.getTR('ecall.row.creation.date'),
					valueGetter: params => {
						const batch = params.data;
						if (!batch) {
							return '';
						}
						// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
						if (batch.rowCreationDate === null || batch.rowCreationDate === undefined) {
							return '';
						}
						return moment(batch.rowCreationDate).format('DD/MM/YYYY');
					},
					field: 'rowCreationDate',
					colId: 'rowCreationDate',
					minWidth: 150,
					suppressHeaderMenuButton: false,
					cellRendererParams: { inputFormat: 'isoDate', outputFormat: 'date' },
					columnType: ColumnType.DATE_COLUMN,
					filter: DatacenterDateFilterComponent,
				})
			);
			this.BATCH_DATA_CENTER_DEFAULT_CONFIGURATION.set(
				'customerReference',
				new CommonColDef<Batch>({
					autoHeight: true,
					headerName: this.translationService.getTR('ecall-off.customer-call-off-reference'),
					valueGetter: params => {
						const batch = params.data;
						if (!batch) {
							return '';
						}
						return batch.customerReference;
					},
					field: 'customerReference',
					colId: 'customerReference',
					minWidth: 150,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					filter: DatacenterTextFilterComponent,
					filterParams: { batchFields: ['customerReference'], removeNoValueFilter: true },

				})
			);
			this.BATCH_DATA_CENTER_DEFAULT_CONFIGURATION.set(
				'callCreator',
				new CommonColDef<Batch>({
					autoHeight: true,
					headerName: this.translationService.getTR('ecall.batch.callCreator'),
					valueGetter: params => {
						const batch = params.data;
						if (!batch) {
							return '';
						}
						return batch.callCreator;
					},
					field: 'callCreator',
					colId: 'callCreator',
					minWidth: 150,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					filter: DatacenterTextFilterComponent,
					filterParams: { batchFields: ['callCreator'], removeNoValueFilter: true },

				})
			);
			this.BATCH_DATA_CENTER_DEFAULT_CONFIGURATION.set(
				'ecallStatus',
				new CommonColDef<Batch>({
					autoHeight: true,
					headerName: this.translationService.getTR('batch.ecall-status'),
					valueGetter: params => {
						const batch = params.data;
						switch (batch?.ecallStatus) {
						case 'O':
							return this.translationService.getTR('ecall-off.open-or-callable');
						case 'C':
							return this.translationService.getTR('ecall-off.closed-or-called');
						default:
							return '';
						}
					},
					field: 'ecallStatus',
					colId: 'ecallStatus',
					minWidth: 150,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					filterParams: { removeNoValueFilter: true },
					filter: DatacenterDropdownFilterComponent,

				})
			);
			this.BATCH_DATA_CENTER_DEFAULT_CONFIGURATION.set(
				'ecallShipToDescription',
				new CommonColDef<Batch>({
					autoHeight: true,
					headerName: this.translationService.getTR('ecall-off.requested-ship-to'),
					valueGetter: params => {
						const batch = params.data;
						if (!batch) {
							return '';
						}
						return batch.ecallShipToDescription;
					},
					field: 'ecallShipToDescription',
					colId: 'ecallShipToDescription',
					minWidth: 150,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					filterParams: { batchFields: ['ecallShipToDescription'], removeNoValueFilter: true },
					filter: DatacenterDropdownFilterComponent,
				})
			);
			this.BATCH_DATA_CENTER_DEFAULT_CONFIGURATION.set(
				'requestedDeliveryDateCallOff',
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('batch.requested-delivery-date'),
					colId: 'requestedDeliveryDateCallOff',
					hide: true,
					sortable: false,
				})
			);
			this.BATCH_DATA_CENTER_DEFAULT_CONFIGURATION.set(
				'requestedDeliveryDateFrom',
				new CommonColDef<Batch>({
					autoHeight: true,
					headerName: this.translationService.getTR('batch.requested-delivery-date-from'),
					valueGetter: params => {
						const batch = params.data;
						if (!batch) {
							return '';
						}
						const requestedDateFrom = batch.requestedDateForCallOffType === 'ASAP'
							? batch.rowCreationDate
							: batch.requestedDeliveryDateFrom;
						// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
						if (requestedDateFrom === null || requestedDateFrom === undefined) {
							return '';
						}
						return moment(requestedDateFrom).format('DD/MM/YYYY');
					},
					field: 'requestedDeliveryDateFrom',
					colId: 'requestedDeliveryDateFrom',
					groupId: 'requestedDeliveryDateCallOff',
					minWidth: 150,
					suppressHeaderMenuButton: false,
					cellRendererParams: { inputFormat: 'isoDate', outputFormat: 'date' },
					columnType: ColumnType.DATE_COLUMN,
					filter: DatacenterDateFilterComponent,
				})
			);
			this.BATCH_DATA_CENTER_DEFAULT_CONFIGURATION.set(
				'requestedDeliveryDateTo',
				new CommonColDef<Batch>({
					autoHeight: true,
					headerName: this.translationService.getTR('batch.requested-delivery-date-to'),
					valueGetter: params => {
						const batch = params.data;
						if (!batch) {
							return '';
						}
						// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
						if (batch.requestedDeliveryDateTo === null
							|| batch.requestedDeliveryDateTo === undefined) {
							return '';
						}
						return moment(batch.requestedDeliveryDateTo).format('DD/MM/YYYY');
					},
					field: 'requestedDeliveryDateTo',
					colId: 'requestedDeliveryDateTo',
					groupId: 'requestedDeliveryDateCallOff',
					minWidth: 150,
					suppressHeaderMenuButton: false,
					cellRendererParams: { inputFormat: 'isoDate', outputFormat: 'date' },
					columnType: ColumnType.DATE_COLUMN,
					filter: DatacenterDateFilterComponent,

				})
			);
			this.BATCH_DATA_CENTER_DEFAULT_CONFIGURATION.set(
				'callOffUnloadingReference',
				new CommonColDef<Batch>({
					autoHeight: true,
					headerName: this.translationService.getTR('ecall-off.unloading-reference'),
					valueGetter: params => {
						const batch = params.data;
						if (!batch) {
							return '';
						}
						return batch.callOffUnloadingReference;
					},
					field: 'callOffUnloadingReference',
					colId: 'callOffUnloadingReference',
					minWidth: 150,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					filter: DatacenterTextFilterComponent,
					filterParams: { batchFields: ['callOffUnloadingReference'], removeNoValueFilter: true },
				})
			);
		}
	}

	private getSupplierData(batch?: Batch) {
		if (!batch?.supplierOrderNumber) {
			return '';
		}
		let value = batch.supplierOrderNumber;

		if (batch.supplierOrderItemNumber) {
			value += `/${ batch.supplierOrderItemNumber }`;
		}
		return value;
	}

	private getMillData(batch?: Batch) {
		if (!batch?.millOrderNumber) {
			return '';
		}
		let value = batch.millOrderNumber;

		if (batch.millOrderItemNumber) {
			value += `/${ batch.millOrderItemNumber }`;
		}
		return value;
	}

	private async downloadInvoice(batch?: Batch) {
		if (!batch?.supplierInvoiceNumber || !batch.invoiceDate) {
			return;
		}
		const invoiceId = await firstValueFrom(this.documentService.getInvoiceByNumberAndDate(
			batch.supplierInvoiceNumber,
			batch.invoiceDate
		));
		if (invoiceId) {
			const invoice = await firstValueFrom(this.documentService.getDocumentDetails(invoiceId, ['id', 'name']));
			window.open(this.documentService.getDownloadDocumentUrl(invoice.id, invoice.name), '_blank');
		}
	}
}

export const DEFAULT_VIEW_BATCHES_DATACENTER: UserDatacenterView = {
	columnState: [
		{
			colId: 'batchReference',
			pinned: 'left'
		},
		{
			colId: 'customerOrderNumber',
			pinned: 'left'
		},
		{
			colId: 'mill'
		},
		{
			colId: 'steelgrade'
		},
		{
			colId: 'qtyGross'
		},
		{
			colId: 'cdd'
		},
		{
			colId: 'nbDaysInStock'
		},
		{
			colId: 'currentLocation'
		},
		{
			colId: 'dispatchAdviceNumber'
		}
	],
	sortModel: [
		{
			colId: 'nbDaysInStock',
			sort: 'desc'
		}
	],
	filterState: [],
	type: 'batch',
	isDefault: true,
	name: 'Default',
	id: -99
};
